import { GFAReference } from "@dune/types";
import { Button, ControlledInput } from "@dune/ui";
import { formatNumber } from "@dune/utils";
import { Plus, Trash2 } from "lucide-react";
import { useEffect } from "react";
import { useFormContext } from "react-hook-form";
import {
	calculateTotalInCa,
	convertCaToHaAca,
	formatHaValue,
	formatWithLeadingZero,
	validateHaInput,
	validateSubUnitInput,
} from "../../utils/gfa/gfa.utils";

const GFAExperiencesContainer = () => {
	const { control, setValue } = useFormContext();
	const { watch } = useFormContext();
	const references = watch("references");

	useEffect(() => {
		if (references && references.length > 0) {
			references.forEach((reference: GFAReference, index: number) => {
				const currentHa = reference.ha;
				const currentA = reference.a;
				const currentCa = reference.ca;

				const paddedHa = formatHaValue(currentHa ?? 0);
				const paddedA = formatWithLeadingZero(currentA ?? 0);
				const paddedCa = formatWithLeadingZero(currentCa ?? 0);

				if (
					typeof currentHa === "number" ||
					(typeof currentHa === "string" && currentHa !== paddedHa)
				) {
					setValue(`references.${index}.ha`, paddedHa);
				}
				if (
					typeof currentA === "number" ||
					(typeof currentA === "string" && currentA !== paddedA)
				) {
					setValue(`references.${index}.a`, paddedA);
				}
				if (
					typeof currentCa === "number" ||
					(typeof currentCa === "string" && currentCa !== paddedCa)
				) {
					setValue(`references.${index}.ca`, paddedCa);
				}
			});
		}
	}, [references, setValue]);

	const handleAreaInputChange = (
		index: number,
		field: "ha" | "a" | "ca",
		value: string,
	) => {
		const isValid =
			field === "ha" ? validateHaInput(value) : validateSubUnitInput(value);
		if (value === "" || isValid) {
			setValue(
				`references.${index}.${field}`,
				value === "" ? "" : Number(value).toString().padStart(2, "0"),
			);
		}
	};

	const totalCa = calculateTotalInCa(references);
	const totalConverted = convertCaToHaAca(totalCa);

	return (
		<div className="flex flex-col gap-4">
			<div className="flex flex-row gap-4">
				<h1 className="text-[#144C65] text-lg font-medium">REFERENCES</h1>
			</div>
			<div className="border rounded-lg p-4 flex flex-col gap-4">
				<div className="flex flex-col gap-4">
					<div className="flex flex-row gap-4 w-[95%]">
						<div className="w-1/6">Section</div>
						<div className="w-1/6">N°</div>
						<div className="w-1/6">Lieu-dit</div>
						<div className="w-1/6">Ha</div>
						<div className="w-1/6">a</div>
						<div className="w-1/6">ca</div>
					</div>
					{references?.map((reference: GFAReference, index: number) => (
						<div
							key={reference.id}
							className="flex flex-row gap-4 items-center"
						>
							<div className="flex flex-row gap-4 w-[95%]">
								<div className="w-1/6">
									<ControlledInput
										name={`references.${index}.section`}
										control={control}
									/>
								</div>

								<div className="w-1/6">
									<ControlledInput
										name={`references.${index}.number`}
										control={control}
									/>
								</div>

								<div className="w-1/6">
									<ControlledInput
										name={`references.${index}.locality`}
										control={control}
									/>
								</div>

								<div className="w-1/6">
									<ControlledInput
										name={`references.${index}.ha`}
										control={control}
										placeholder="00"
										type="number"
										onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
											handleAreaInputChange(index, "ha", e.target.value);
										}}
									/>
								</div>

								<div className="w-1/6">
									<ControlledInput
										name={`references.${index}.a`}
										control={control}
										placeholder="00"
										maxLength={2}
										onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
											handleAreaInputChange(index, "a", e.target.value);
										}}
									/>
								</div>

								<div className="w-1/6">
									<ControlledInput
										name={`references.${index}.ca`}
										control={control}
										placeholder="00"
										maxLength={2}
										onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
											handleAreaInputChange(index, "ca", e.target.value);
										}}
									/>
								</div>
							</div>
							<Trash2
								onClick={() => {
									setValue("references", [
										...references.slice(0, index),
										...references.slice(index + 1),
									]);
								}}
							/>
						</div>
					))}

					{/* Total calculation display */}
					{references && references.length > 0 && (
						<div className="border-t pt-4 mt-2">
							<div className="flex flex-row gap-4 w-[95%] font-medium text-[#144C65]">
								<div className="w-1/6" />
								<div className="w-1/6" />
								<div className="w-1/6 text-right">Total:</div>
								<div className="w-1/6 text-center">
									{!Number.isNaN(Number(totalConverted.ha))
										? totalConverted.ha < 10
											? formatWithLeadingZero(totalConverted.ha)
											: formatNumber(totalConverted.ha.toString())
										: totalConverted.ha}
								</div>
								<div className="w-1/6 text-center">
									{formatWithLeadingZero(totalConverted.a)}
								</div>
								<div className="w-1/6 text-center">
									{formatWithLeadingZero(totalConverted.ca)}
								</div>
							</div>
						</div>
					)}

					<Button
						onClick={() => {
							setValue("references", [
								...references,
								{
									id: references.length + 1,
									ha: undefined,
									a: undefined,
									ca: undefined,
								},
							]);
						}}
						variant="none"
						size="none"
						className="self-start"
						type="button"
					>
						<div className="flex flex-row items-center gap-2 font-medium text-sm text-primary">
							<Plus /> Ajouter
						</div>
					</Button>
				</div>
			</div>
		</div>
	);
};

export default GFAExperiencesContainer;
